import { ComponentDescription, ComponentInstance } from '@sapappgyver/appgyver-descriptors/lib/dolan/v4';
import { lruMemoize } from '@reduxjs/toolkit';
import isContainerComponentDescription from './isContainerComponentDescription';
import getComponentInstanceParentMappingByIds from './getComponentInstanceParentMappingByIds';

export type ComponentDropPositions = Record<string, ComponentDropPosition>;

export interface ComponentDropPosition {
  indexes: number[];
  depth: number;
}

const empty: never[] = [];

function getComponentDirectDropPositions(
  parentComponent: ComponentInstance,
  currentParentComponent: ComponentInstance | undefined,
) {
  const { children } = parentComponent;
  if (currentParentComponent?.name === 'layout' && parentComponent.name !== 'layout') {
    // A Row component cell cannot be dragged to a non-Row component
    return empty;
  }
  if (parentComponent.name === 'layout' && currentParentComponent?.name !== 'layout') {
    // Cannot drag directly into a Row component, unless dragging a Row cell
    return empty;
  }
  // NOTE: Index 0 is before the first child, index equal to children length is after the last child
  const dropIndexes: number[] = [];
  // Each position before each existing children
  for (let index = 0; index <= children.length; index += 1) {
    dropIndexes.push(index);
  }
  return dropIndexes;
}

/**
 * For the given component structure, returns all the possible positions where the given component instance
 * could be inserted, e.g. drag'n'dropped. The return value is an mapping where keys are possible container
 * IDs and values are arrays of allowed insertion positions. If a component ID is not present in the mapping,
 * or the array of positions is empty, the component cannot be inserted into that container.
 *
 * This function prevents:
 * - inserting the component into itself
 * - adding anything inside nested composite components (only insertion into the root is allowed)
 * - messing cells in a Row component
 *
 * This function also requires the mapping of component descriptions, as the description affects
 * how the component accepts children.
 */
const getComponentDropPositions = lruMemoize(
  (
    rootComponent: ComponentInstance,
    insertedComponentId: string | null,
    descriptions: Record<string, ComponentDescription | undefined>,
  ): ComponentDropPositions => {
    const parentsById = getComponentInstanceParentMappingByIds(rootComponent);
    const currentParentComponent = insertedComponentId == null ? undefined : parentsById[insertedComponentId];
    const dropPositions: ComponentDropPositions = {};

    function traverse(parentComponent: ComponentInstance, depth: number) {
      const { id } = parentComponent;
      const parentComponentDescription = descriptions[parentComponent.name];
      if (parentComponentDescription == null || !isContainerComponentDescription(parentComponentDescription)) {
        // This is not a container, so we cannot insert anything into it
        return;
      }
      if (parentComponentDescription.composite_json && depth > 0) {
        // Cannot insert anything into a nested composite component
        return;
      }
      if (id === insertedComponentId) {
        // Cannot insert the component into itself
        return;
      }
      const indexes = getComponentDirectDropPositions(parentComponent, currentParentComponent);
      dropPositions[id] = { indexes, depth };
      // Traverse children
      parentComponent.children.forEach((child) => traverse(child, depth + 1));
    }

    traverse(rootComponent, 0);
    return dropPositions;
  },
);

export default getComponentDropPositions;
